import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sunrise, Sun, Sunset, Moon } from "lucide-react";

const PARTS = {
  matin: { key: "matin", Icon: Sunrise, from: "#f59e0b", to: "#fb923c", glow: "245,158,11" },
  aprem: { key: "aprem", Icon: Sun, from: "#06b6d4", to: "#3b82f6", glow: "6,182,212" },
  soir: { key: "soir", Icon: Sunset, from: "#f97316", to: "#ec4899", glow: "236,72,153" },
  nuit: { key: "nuit", Icon: Moon, from: "#8b5cf6", to: "#6366f1", glow: "139,92,246" },
};

function partOf(h) {
  if (h >= 6 && h < 12) return PARTS.matin;
  if (h >= 12 && h < 18) return PARTS.aprem;
  if (h >= 18 && h < 22) return PARTS.soir;
  return PARTS.nuit;
}

const pad = (n) => String(n).padStart(2, "0");
const R = 44;
const C = 2 * Math.PI * R;

export function SmartClock({ className = "" }) {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const h = now.getHours(), m = now.getMinutes(), s = now.getSeconds();
  const part = partOf(h);
  const partLabel = t(`clock.${part.key}`);
  const Icon = part.Icon;
  const locale = (i18n.language || "fr").startsWith("nl") ? "nl-BE" : (i18n.language || "fr").startsWith("en") ? "en-GB" : "fr-BE";
  const dateStr = now.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
  const offset = C * (1 - s / 60);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="smart-clock"
      className={`relative overflow-hidden glass rounded-3xl card-3d px-5 py-4 flex items-center gap-5 ${className}`}
      style={{ boxShadow: `0 18px 50px -22px rgba(${part.glow},0.55)` }}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-2xl opacity-40"
        style={{ background: `radial-gradient(circle, ${part.to}, transparent 70%)` }}
      />
      {/* Analog seconds ring */}
      <div className="relative h-[92px] w-[92px] shrink-0">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <defs>
            <linearGradient id="clockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={part.from} />
              <stop offset="100%" stopColor={part.to} />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="5" />
          <circle
            cx="50" cy="50" r={R} fill="none" stroke="url(#clockGrad)" strokeWidth="5" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={offset}
            style={{ transition: s === 0 ? "none" : "stroke-dashoffset 0.9s linear" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            key={partLabel}
            initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
            className="flex items-center justify-center h-9 w-9 rounded-full"
            style={{ background: `linear-gradient(135deg, ${part.from}, ${part.to})` }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.span>
          <span className="mt-1 font-mono text-[10px] tabular-nums text-muted-foreground">{pad(s)}s</span>
        </div>
      </div>
      {/* Digital time */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: part.to }}>
            {partLabel}
          </span>
        </div>
        <div className="font-display font-extrabold leading-none tracking-tight tabular-nums mt-1 flex items-baseline">
          <span className="text-4xl sm:text-5xl text-white">{pad(h)}</span>
          <span className="text-4xl sm:text-5xl mx-0.5" style={{ color: part.to, opacity: s % 2 ? 0.3 : 1, transition: "opacity 0.3s" }}>:</span>
          <span className="text-4xl sm:text-5xl text-white">{pad(m)}</span>
        </div>
        <p className="text-sm text-muted-foreground capitalize mt-1.5">{dateStr}</p>
      </div>
    </motion.div>
  );
}
